import {observer} from "mobx-react";
import {Card, Spin, Steps, Space} from "antd";
import axios from "axios";
import React, {useEffect, useState} from 'react';
import FakeTable from "../../components/FakeTable";
import ScoreTag from "../../components/ScoreTag";
import StatusTag from "../../components/StatusTag";
import {pp, divPrint, callWithCatch} from "../../utils";
import {Knockout4, Knockout4with56, Knockout5, Knockout6, KnockoutFlex} from "./KnockOut";
import {ModalBtn, NiceModalForm} from "../../components/Modals";
import Device from "../../components/Device";
import NiceModal from "@ebay/nice-modal-react";

let H2hTable = observer(({table, allstate}) => {
  return <Card size="small"  title="Matches (row vs column)" className="narrow-card scroll-card">
    <FakeTable className="mlp-table-results"
               style={{width: table.length*(Device.xs ? 80 : 120) + "px", tableLayout: "fixed", fontSize: Device.xs && "10px"}}
               objs={table}
               tds={r => {
      return r.map((c, i) => {
        if (typeof c === "string")
          return c.split(/\n/).map(s => <div key={s}>{s}</div>);
        else {
          if (c.w === 0 && c.l === 0)
            return null;
          else {
            let color = c.w > c.l ? "win" : "lose";
            if (c.w === c.l)
              color = "invalid";

            let v = [c.w, c.l].join(" - ")

            if (c.w === 1 && c.l === 0 && !allstate)
              v = "W";

            if (c.w === 0 && c.l === 1 && !allstate)
              v = "L";

            return <ScoreTag type={color} v={v}/>
          }
        }
      })
    }} />
  </Card>
});

let NewView = observer(({division, url}) => {
  let [stats, setStats] = useState(null);

  let load = async () => {
    let data = (await axios.get(url || `/pools/${division.id}/stats`)).data;

    setStats(data.stats);
  };

  useEffect(() => {
    load();
  }, []);

  if (!stats || !stats.standing || !stats.standing[0])
    return <Card size="small" title="Leaderboard" className="narrow-card scroll-card not-confusing-alert">
      <Spin />
    </Card>

  let standingExtra;
  if (!stats.standing[0][1]) {
    let msg = "Partial Results";
    if (stats.standing[0][1] === 0)
      msg = "No Match Results Yet";
    standingExtra = <StatusTag type="warning" message={msg} />;
  }
  else
    standingExtra = <StatusTag type="success" message="Final Results" />;

  let headers = stats.headers;

  return <div className="space-y-8">
    <Card size="small"  title="Leaderboard" className="narrow-card scroll-card not-confusing-alert" extra={standingExtra}>
      <FakeTable small className="division-leaderboard-table" style={{width: "100%", minHeight: "400px"}}>
        <tr>
          <td style={{width: "10px"}}></td>
          <td>
            <div className="block md:hidden text-xs text-gray-500">
              Scroll right to see all columns →
            </div>
          </td>
          {headers.map(h => <td key={h[0]} style={{width: h[1].length > 10 ? "125px" : "100px", textAlign: "center"}}>
            <div>{h[0]}</div>
            <div className="subtitle">{h[1]}</div>
          </td>)}
        </tr>
        {stats.standing.map((row, i) => {
          return <tr key={row.id}>
            <td>
              <div>{i+1}</div>
              <div className="no-extra">-</div>
            </td>
            <td>
              <div><b>{row.name}</b></div>
              <div className="no-extra">-</div>
            </td>
            {row.columns.map((c, i) => {
              if (!_.isArray(c))
                c = [c, null];

              // Get the corresponding header for this column (accounting for filtering)
              let width = headers[i][3] === "long" ? "80px" : "55px";

              return <td key={i}>
                <div style={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center"}}>
                  <ScoreTag type={i === row.mark ? "info" : "invalid"} v={c[0]} w={width}/>
                  <div className={c[1] ? `yes-extra-3` : "no-extra"}>{c[1] || "-"}</div>
                </div>
              </td>
            })}
          </tr>
        })}
      </FakeTable>
    </Card>
    <H2hTable table={stats.table} allstate={stats.headers.some(h => h[1] === "Courts Won")} />
    <Card size="small" title="Ranking Order (see columns above)">
      <div className="p-4">
        <Steps current={1} className="ranking-explained">
          {stats.headers.map((h, i) => <Steps.Step key={i} status="process" title={h[0]} description={h[2]} />)}
        </Steps>
      </div>
    </Card>
  </div>
});


let As6Knockout = observer(({division, url, teamById}) => {
  return <div>
    <Knockout6 division={division} teamById={teamById}/>
  </div>
});

let As4Knockout = observer(({division, url, teamById}) => {
  return <div>
    <Knockout4 division={division} teamById={teamById}/>
  </div>
});

export const PlayoffBracket = NiceModal.create(({ pool, onMatchClick }) => {
  const [lanes, setLanes] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    callWithCatch({
      url: `/pools/${pool.id}/bracket_view`,
      method: 'get',
      setXhr: setLoading,
      onSuccess: data => {
        setLanes(data.lanes);
      }
    });
  }, [pool]);

  let print = () => {
    divPrint(document.getElementById('playoff-bracket-view'));
  }

  let title = <Space>
    <span>Bracket View</span>
    <a onClick={print}>print</a>
  </Space>

  let width = 600;
  if (lanes)
    width = lanes[0].brackets.length * 300;

  return <NiceModalForm
    top
    title={title}
    width={width + "px"}
    footer={null}
    style={{top: 20}}>
    <div id="playoff-bracket-view" className="bg-white overflow-x-auto" style={{minHeight: "400px"}}>
      {loading ? <Spin /> : <KnockoutFlex lanes={lanes} onMatchClick={onMatchClick} />}
    </div>
  </NiceModalForm>
});

export const Leaderboard = observer(({division, url, teamById, ...props}) => {
  if (division.bracket_view || division.kind === "playoff")
    return <KnockoutFlex lanes={division.bracket_view} />

  if (division?.knockout) {
    if (division.team_ids.length === 5)
      return <Knockout5 division={division} url={url} teamById={teamById}/>

    if (division.team_ids.length === 6)
      if (division.play_format === "playoff_4_56_alone")
        return <Knockout4with56 division={division} url={url} teamById={teamById}/>
      else
      return <As6Knockout division={division} url={url} teamById={teamById}/>
    else
      return <As4Knockout division={division} url={url} teamById={teamById}/>
  }
  else
    return <NewView division={division} url={url} {...props} />
});

export const LeaderboardBtn = observer(({division, url, teamById, title}) => {
  return <ModalBtn footer={null} width={800} text={title || "Leaderboard"}>
    <Leaderboard division={division} url={url} teamById={teamById} />
  </ModalBtn>;
});



export default Leaderboard